/**
 * PaymentMethodsPage (결제수단 관리 화면)
 *
 * 결제수단 목록 조회 및 CRUD 작업을 수행하는 페이지
 *
 * 책임:
 * - 결제수단 목록 표시
 * - 결제수단 추가/수정/삭제
 * - 결제수단 상태 표시 (활성/비활성)
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePaymentMethodCRUD } from '../hooks/usePaymentMethodCRUD'
import { PaymentMethodFormModal } from '../components/PaymentMethodFormModal'
import type { PaymentMethod } from '../types/PaymentMethod'
import { useUsers } from '@/domains/user/hooks/useUsers'
import { useToast } from '@/shared/components/ui/Toast'

export function PaymentMethodsPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const { data, loading, error, loadAll, create, update, remove } =
    usePaymentMethodCRUD()

  // 사용자 목록 조회
  const { users } = useUsers()

  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  /**
   * 초기 데이터 로드
   */
  useEffect(() => {
    loadAll()
  }, [])

  /**
   * ownerId로 사용자 이름 조회
   */
  const getUserName = (ownerId: string): string => {
    const user = users.find(u => u.id === ownerId)
    return user ? user.name : '알 수 없음'
  }

  /**
   * 새 결제수단 추가 시작
   */
  const handleAdd = () => {
    setEditingId(null)
    setShowModal(true)
  }

  /**
   * 결제수단 수정 시작
   */
  const handleEdit = (paymentMethod: PaymentMethod) => {
    setEditingId(paymentMethod.id)
    setShowModal(true)
  }

  /**
   * 결제수단 삭제
   */
  const handleDelete = async (id: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return

    try {
      await remove(id)
      toast.success('삭제되었습니다')
      await loadAll()
    } catch (err) {
      console.error('결제수단 삭제 실패:', err)
      toast.error(`삭제 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    }
  }

  /**
   * 모달 닫기
   */
  const handleCloseModal = () => {
    setShowModal(false)
    setEditingId(null)
  }

  /**
   * 결제수단 저장 (생성/수정)
   */
  const handleSave = async (input: Omit<PaymentMethod, 'id'>) => {
    try {
      if (editingId) {
        await update(editingId, input)
        toast.success('수정되었습니다')
      } else {
        await create(input)
        toast.success('추가되었습니다')
      }
      await loadAll()
      handleCloseModal()
    } catch (err) {
      console.error('결제수단 저장 실패:', err)
      toast.error(`저장 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    }
  }

  /**
   * 로딩 상태
   */
  if (loading && data.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-500 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">결제수단을 불러오는 중...</p>
        </div>
      </div>
    )
  }

  /**
   * 에러 상태
   */
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full">
          <div className="text-center mb-4">
            <span className="text-6xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
            데이터를 불러올 수 없습니다
          </h2>
          <p className="text-red-600 dark:text-red-400 text-sm text-center mb-4">
            {error.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
          >
            다시 시도
          </button>
        </div>
      </div>
    )
  }

  /**
   * 메인 컨텐츠
   */
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* 헤더 */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4">
        <button
          onClick={() => navigate('/settings')}
          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-2 flex items-center gap-1"
        >
          ← 뒤로 가기
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">결제수단 관리</h1>
      </div>

      {/* 결제수단 목록 */}
      <div className="px-4 py-6 space-y-3">
        {data.length === 0 ? (
          <div className="text-center py-12">
            <span className="text-6xl block mb-4">💳</span>
            <p className="text-gray-600 dark:text-gray-400 mb-4">등록된 결제수단이 없습니다</p>
            <button
              onClick={handleAdd}
              className="px-6 py-3 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
            >
              결제수단 추가하기
            </button>
          </div>
        ) : (
          data.map((pm) => (
            <div
              key={pm.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 flex items-center justify-between"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{pm.name}</h3>
                  <span className="text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full">
                    {getUserName(pm.ownerId)}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {getPaymentTypeLabel(pm.type)}
                  {pm.memo && ` · ${pm.memo}`}
                </p>
                {!pm.isActive && (
                  <span className="text-xs text-red-600 dark:text-red-400">비활성</span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(pm)}
                  className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded transition-colors"
                >
                  수정
                </button>
                <button
                  onClick={() => handleDelete(pm.id)}
                  className="px-3 py-1 text-sm bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded transition-colors"
                >
                  삭제
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 추가 버튼 (목록이 있을 때만) */}
      {data.length > 0 && (
        <div className="fixed bottom-20 right-4">
          <button
            onClick={handleAdd}
            className="w-14 h-14 bg-primary-600 dark:bg-primary-500 text-white rounded-full shadow-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors flex items-center justify-center text-2xl"
          >
            +
          </button>
        </div>
      )}

      {/* 추가/수정 모달 */}
      <PaymentMethodFormModal
        show={showModal}
        editingPaymentMethod={
          editingId ? data.find((pm) => pm.id === editingId) : null
        }
        onClose={handleCloseModal}
        onSave={handleSave}
      />
    </div>
  )
}

/**
 * 결제수단 유형 한글 레이블
 */
function getPaymentTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    cash: '현금',
    debit_card: '체크카드',
    credit_card: '신용카드',
    simple_pay: '간편결제',
    bank_transfer: '계좌이체',
  }
  return labels[type] || type
}
